import { custom } from "viem";

import type {
  WorkerBatchResponse,
  WorkerMessage,
  WorkerResponse,
} from "./types.js";

/** Default timeout for worker requests (2 minutes — Tevm is slower than Anvil). */
const DEFAULT_TIMEOUT_MS = 120_000;

/** Max requests to batch before flushing. */
const BATCH_MAX_SIZE = 64;

/** Time window in ms to collect requests before flushing. */
const BATCH_WAIT_MS = 50;

/** Max automatic retries for transient RPC errors. */
const MAX_RETRIES = 2;

/** Base delay between retries in ms. */
const RETRY_BASE_DELAY_MS = 500;

/** Check if an error message indicates a transient/retryable failure. */
function isRetryableError(message: string): boolean {
  return (
    message.includes("old data not available due to pruning") ||
    message.includes("missing trie node") ||
    message.includes("rate limit") ||
    message.includes("429") ||
    message.includes("Too Many Requests") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("Internal error") ||
    message.includes("internal server error") ||
    message.includes("timed out") ||
    message.includes("ECONNRESET") ||
    message.includes("ETIMEDOUT") ||
    message.includes("socket hang up")
  );
}

/**
 * Create a viem custom transport that routes JSON-RPC calls through a Web Worker.
 *
 * Uses a monotonic request ID and pending promise map for correlation.
 * Includes micro-batching: requests within a 50ms window are collected and
 * sent as a single batch message to the worker for concurrent processing.
 *
 * Transient errors (rate limits, server errors, pruning) are automatically
 * retried up to MAX_RETRIES times with exponential backoff.
 */
export function createTevmTransport(
  worker: Worker,
  timeoutMs = DEFAULT_TIMEOUT_MS,
) {
  let nextId = 1;
  const pending = new Map<
    number,
    {
      resolve: (value: unknown) => void;
      reject: (reason: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  // Micro-batching queue
  let batchQueue: WorkerMessage[] = [];
  let batchTimer: ReturnType<typeof setTimeout> | null = null;

  function flushBatch() {
    batchTimer = null;
    if (batchQueue.length === 0) return;

    const batch = batchQueue;
    batchQueue = [];
    worker.postMessage(batch);
  }

  function enqueue(message: WorkerMessage) {
    batchQueue.push(message);

    if (batchQueue.length >= BATCH_MAX_SIZE) {
      if (batchTimer !== null) {
        clearTimeout(batchTimer);
        batchTimer = null;
      }
      flushBatch();
    } else {
      batchTimer ??= setTimeout(flushBatch, BATCH_WAIT_MS);
    }
  }

  worker.addEventListener(
    "message",
    (event: MessageEvent<WorkerResponse | WorkerBatchResponse>) => {
      const data = event.data;

      // Handle batch responses (array of responses)
      if (Array.isArray(data)) {
        for (const item of data) {
          resolveResponse(item);
        }
        return;
      }

      // Handle single response
      resolveResponse(data);
    },
  );

  function resolveResponse(data: WorkerResponse) {
    // Skip non-RPC responses (init/ready/error messages)
    if (!("id" in data) || typeof data.id !== "number") return;

    const entry = pending.get(data.id);
    if (!entry) return;

    clearTimeout(entry.timer);
    pending.delete(data.id);

    if (data.error) {
      entry.reject(new Error(data.error.message));
    } else {
      entry.resolve(data.result);
    }
  }

  /** Send a single RPC request through the worker (no retry). */
  function sendRequest(method: string, params: unknown[]): Promise<unknown> {
    const id = nextId++;
    const message: WorkerMessage = {
      id,
      method,
      params,
    };

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(
          new Error(
            `Tevm Worker request timed out after ${String(timeoutMs)}ms: ${method}`,
          ),
        );
      }, timeoutMs);

      pending.set(id, { resolve, reject, timer });
      enqueue(message);
    });
  }

  return custom({
    async request({ method, params }: { method: string; params?: unknown }) {
      const resolvedParams = (params ?? []) as unknown[];

      // Retry loop for transient errors
      let lastErr: Error | undefined;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          return await sendRequest(method, resolvedParams);
        } catch (err) {
          lastErr = err instanceof Error ? err : new Error(String(err));

          // Only retry on transient errors
          if (attempt < MAX_RETRIES && isRetryableError(lastErr.message)) {
            const delay = RETRY_BASE_DELAY_MS * 2 ** attempt;
            await new Promise((r) => setTimeout(r, delay));
            continue;
          }

          throw lastErr;
        }
      }

      // Should not reach here, but satisfy TS
      throw lastErr ?? new Error("Request failed");
    },
  });
}
